import { Redirect } from 'expo-router';
import Head from 'expo-router/head';
import { observer } from 'mobx-react-lite';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';

import { GiftingWebLanding } from '@/components/gifting/GiftingWebLanding';
import {
  CAMPAIGN_OG_DESCRIPTION,
  CAMPAIGN_OG_IMAGE,
  CAMPAIGN_OG_IMAGE_HEIGHT,
  CAMPAIGN_OG_IMAGE_WIDTH,
  CAMPAIGN_OG_TITLE,
  CAMPAIGN_OG_URL,
} from '@/constants/giftingOg';
import { colors } from '@/constants/theme';
import { giftingRouteFor } from '@/lib/giftingRoute';
import { authStore } from '@/stores/auth.store';

/**
 * Campaign entry: app.marketpryce.com/season-of-gifting (universal link —
 * opens the native app when installed, this web page otherwise).
 *
 * Web signups/claims are OPEN (founder call 2026-08-04 — reverses the
 * earlier app-only decision): the App Store cliff dropped the link context
 * and the referrer, so new users now complete the funnel in the browser
 * and get pushed to download the app from the secured screen.
 *
 * The landing IS the campaign page on both platforms now — there used to be a
 * separate intro screen behind it running the same headline, CTA, and product
 * grid a second time.
 *
 * Branches (each tested in __tests__/index.test.tsx):
 *   1. web, authed             → wherever the SERVER says this user is in the
 *                                funnel (gifting_next_step)
 *   2. web otherwise           → landing (marketing + "Claim my gift" into
 *                                the funnel when live; download-only dark)
 *   3. native, not hydrated    → spinner (never flash the wrong surface;
 *                                same guard as app/index.tsx)
 *   4. native, authed          → same server-owned step
 *   5. native, otherwise       → landing (its own dark/not-eligible states)
 */
const SeasonOfGiftingIndex = observer(() => {
  if (Platform.OS === 'web') {
    const authedTarget =
      authStore.hydrated && authStore.isAuthenticated ? giftingRouteFor(authStore.user) : null;
    return (
      <>
        <Head>
          <title>{CAMPAIGN_OG_TITLE}</title>
          <meta name="description" content={CAMPAIGN_OG_DESCRIPTION} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={CAMPAIGN_OG_TITLE} />
          <meta property="og:description" content={CAMPAIGN_OG_DESCRIPTION} />
          <meta property="og:image" content={CAMPAIGN_OG_IMAGE} />
          {/* Canonical is /campus-drop for BOTH routes: it is the path we
              share, and the one that actually resolves as a static file.
              Scrapers treat og:url as canonical and dedupe on it. */}
          <meta property="og:url" content={CAMPAIGN_OG_URL} />
          {/* Route-level twitter:* overrides: X prefers twitter tags, and the
              root layout's are the generic app pitch. Same for image dims. */}
          <meta property="og:image:width" content={CAMPAIGN_OG_IMAGE_WIDTH} />
          <meta property="og:image:height" content={CAMPAIGN_OG_IMAGE_HEIGHT} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={CAMPAIGN_OG_TITLE} />
          <meta name="twitter:description" content={CAMPAIGN_OG_DESCRIPTION} />
          <meta name="twitter:image" content={CAMPAIGN_OG_IMAGE} />
        </Head>
        {/* Pre-hydration the landing renders for everyone ON PURPOSE: it is
            the static-export body (scrapers/no-JS see marketing, not a
            spinner). Authed+claimed users get a brief landing flash before
            the redirect — accepted tradeoff. */}
        {authedTarget ? <Redirect href={authedTarget} /> : <GiftingWebLanding />}
      </>
    );
  }

  if (!authStore.hydrated) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator color={colors.deepGreen} />
      </View>
    );
  }

  // null = the server says this account has no step in the funnel right now
  // (campaign closed, or not on the allowlist). Fall through to the landing so
  // they get the "for college athletes" state — redirecting anywhere would
  // bounce straight back here.
  const authedTarget = authStore.isAuthenticated ? giftingRouteFor(authStore.user) : null;
  if (authedTarget) {
    return <Redirect href={authedTarget} />;
  }

  return <GiftingWebLanding />;
});

export default SeasonOfGiftingIndex;

const styles = StyleSheet.create({
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background,
  },
});
